import React, { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Modal, Form, Select, DatePicker, Tooltip } from 'antd'
import {
  Arrow,
  FormCard,
  FormItem,
  Heading,
  Input,
  TextArea
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import Delivery from '../iconsV2/Delivery'
import { Button, Text } from '@lmc/ui-kit/src/general'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import Asterix from 'screens/Rostering/RotaPattern/components/Asterix'
import StockBanner from './StockBanner'
import AdditionalDetailsSection from './AdditionalDetailsSection'

type DeliveryModalProps = {
  open: boolean
  medicationName: string
  expectedStock: number
  onCancel: () => void
  onSave: (data: DeliveryData) => void
}

export type DeliveryData = {
  description: string
  receivedStock: number
  newStock: number
  isStockAccurate: boolean
  resolvedBy: string
  receiveDate: string
  resolveDiscrepancy: string | null
  discrepancyReason: string
  receivedFrom: string
  witnessedBy: string
  witnessPin: string
  additionalDetails: boolean
  media?: File[]
}

const DeliveryModal: React.FC<DeliveryModalProps> = ({
  open,
  medicationName,
  expectedStock,
  onCancel,
  onSave
}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const theme = useTheme()
  const [form] = Form.useForm()
  const [receivedStock, setReceivedStock] = useState<number>(0)
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false)
  const [fileList, setFileList] = useState<any[]>([])
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null)
  const [resolveDiscrepancy, setResolveDiscrepancy] = useState<string | null>(
    null
  )
  const [discrepancyReason, setDiscrepancyReason] = useState<string>('')

  const handleFileChange = (info: any) => {
    setFileList(info.fileList)
  }

  const handleReceivedStockChange = (value: string) => {
    const numVal = Number(value)

    if (isNaN(numVal)) {
      setReceivedStock(0)
      setAdjustmentError(null)
      return
    }

    if (numVal <= 0) {
      setAdjustmentError(t('INVALID_INPUT'))
      setReceivedStock(0)
    } else {
      setAdjustmentError(null)
      setReceivedStock(numVal)
    }
  }

  const handleSave = () => {
    form.validateFields().then((values) => {
      const newStock = expectedStock + receivedStock
      const data: DeliveryData = {
        description: values.description || '',
        receivedStock: receivedStock,
        newStock: newStock,
        isStockAccurate: false,
        resolvedBy: values.resolvedBy || '',
        receiveDate: values.receiveDate?.format('DD-MM-YYYY') || '',
        resolveDiscrepancy,
        discrepancyReason,
        receivedFrom: values.receivedFrom || '',
        witnessedBy: values.witnessedBy || '',
        witnessPin: values.witnessPin || '',
        additionalDetails: showAdditionalDetails
      }
      onSave(data)
      form.resetFields()
      setReceivedStock(0)
      setShowAdditionalDetails(false)
      setAdjustmentError(null)
      setResolveDiscrepancy(null)
      setDiscrepancyReason('')
    })
  }

  const newStock = expectedStock + receivedStock

  const isAdjustmentError = adjustmentError !== null

  const newStockValue = receivedStock === 0 || isAdjustmentError ? '' : newStock

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
      title={
        <TitleContainer>
          <Delivery />
          <ModalTitle>
            <Heading level={3}>{t('DELIVERY')}</Heading>
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <ModalContentContainer>
        <Form form={form}>
          <FormCard level={2}>
            <FormItem
              name="description"
              label={<Text>{t('DESCRIPTION')}</Text>}
              full
              rules={[
                { required: true, message: t('DESCRIPTION_REQUIRED') },
                { max: 150, message: t('DESCRIPTION_MAX_LENGTH') }
              ]}
            >
              <StyledTextArea
                rows={4}
                placeholder={t('ENTER_DESCRIPTION')}
                maxLength={150}
                showCount
                data-testid="delivery-description"
              />
            </FormItem>
          </FormCard>
        </Form>

        <FormCard
          level={2}
          title={
            <CardTitleContainer>
              <MedicationNameHeading weight="bold" level={5}>
                {medicationName}
              </MedicationNameHeading>
            </CardTitleContainer>
          }
        >
          <AdjustmentContainer>
            <CalculationContainer>
              <CalculationColumn $align="center">
                <Label>{t('EXPECTED_STOCK')}</Label>
                <Value weight="bold">
                  {expectedStock} {t('TABLETS')}
                </Value>
              </CalculationColumn>
              <Arrow direction="right" size={18} />
              <CalculationColumn>
                <LabelContainer>
                  <Asterix />
                  <Label>{t('RECEIVED_STOCK')}</Label>
                </LabelContainer>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={receivedStock}
                  onChange={(e) => handleReceivedStockChange(e.target.value)}
                  status={adjustmentError ? 'error' : undefined}
                  style={{ width: '100%' }}
                  data-testid="received-stock-input"
                  suffix={t('TABLETS')}
                />
              </CalculationColumn>
              <Arrow direction="right" size={18} />
              <CalculationColumn $align="center">
                <Label>{t('NEW_STOCK')}</Label>
                <DiscrepancyValue
                  $isPositive={true}
                  $noDiscrepancy={true}
                  weight="bold"
                >
                  {newStockValue} {t('TABLETS')}
                </DiscrepancyValue>
              </CalculationColumn>
            </CalculationContainer>
            {adjustmentError && (
              <StockBanner type="error">{adjustmentError}</StockBanner>
            )}
          </AdjustmentContainer>
        </FormCard>

        <FormCard level={2}>
          <FormItem
            name="resolvedBy"
            label={<Text>{t('RECEIVED_BY')}</Text>}
            required
            rules={[{ required: true, message: t('RECEIVED_BY_REQUIRED') }]}
          >
            <Select placeholder={t('SELECT_PERSON')} style={{ width: '100%' }}>
              <Select.Option value="user1">Richard K. Oates</Select.Option>
            </Select>
          </FormItem>

          <FormItem
            name="receiveDate"
            label={<Text>{t('RECEIVE_DATE')}</Text>}
            required
            rules={[{ required: true, message: t('RECEIVE_DATE_REQUIRED') }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder={t('SELECT_DATE')}
              format="DD-MM-YYYY"
            />
          </FormItem>

          <FormItem
            name="receivedFrom"
            label={
              <LabelWithIcon>
                <Text>{t('RECEIVED_FROM')}</Text>
                <Tooltip
                  title={t('RECEIVED_FROM_TOOLTIP')}
                  color={theme.color.neutral.color3}
                  overlayInnerStyle={{
                    color: '#fff',
                    borderRadius: '9px',
                    padding: '10px 16px'
                  }}
                >
                  <QuestionIcon>
                    <QuestionMarkIcon />
                  </QuestionIcon>
                </Tooltip>
              </LabelWithIcon>
            }
            required
            rules={[{ required: true, message: t('RECEIVED_FROM_REQUIRED') }]}
          >
            <Input placeholder={t('TYPE_HERE')} />
          </FormItem>
        </FormCard>

        <AdditionalDetailsSection
          showAdditionalDetails={showAdditionalDetails}
          onToggle={setShowAdditionalDetails}
          fileList={fileList}
          onFileChange={handleFileChange}
        />
      </ModalContentContainer>

      <Divider />

      <ButtonsContainer>
        <Button onClick={onCancel} data-testid="cancel-btn">
          {t('CANCEL')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!!adjustmentError}
          data-testid="save-btn"
        >
          {t('SAVE')}
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const MedicationNameHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CardTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const CalculationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`

const CalculationColumn = styled.div<{ $align?: 'left' | 'center' }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 0 8px;
  gap: 8px;
  align-items: ${({ $align = 'left' }) => $align};
`

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const LabelWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const QuestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.neutral.color1};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`

const Label = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  min-height: 24px;
`

const Value = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const DiscrepancyValue = styled(Text)<{
  $isPositive: boolean
  $noDiscrepancy?: boolean
}>`
  color: ${({ $noDiscrepancy, theme }) =>
    $noDiscrepancy ? theme.color.status.success : theme.color.status.warning};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const StyledTextArea = styled(TextArea)`
  width: 100%;
  margin-bottom: 16px;
`

const AdjustmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

export default DeliveryModal
